import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { EventPhase, LiveWineView } from '@gwc/shared';
import { tastingsApi, type Registration } from '../api/tastings';
import { revealApi } from '../api/reveal';
import { UnveilRegistration } from './UnveilRegistration';
import { WineColorChip } from './WineColorChip';

/**
 * The host's two-step reveal for one letter, as it's run at the table:
 *
 *   "In seventh place, wine B, with 56.5 points" → **Reveal wine**: the label and
 *   the details go up on every phone and the room guesses →
 *   **Who brought this?** → the name goes up.
 *
 * Who brought it comes from the letter's registration (bagged ahead of time or
 * picked at step one); the host can correct it before showing it. Someone who
 * brought a second bottle is picked by name — their registration is already on
 * another letter.
 */
export function RevealControls({
  eventId,
  phase,
  wine,
  registrations,
  letterByRegistration,
  onChange,
}: {
  eventId: string;
  phase: EventPhase;
  wine: LiveWineView;
  registrations: Registration[];
  /** Registration id → the letter it's on, for every assigned letter. */
  letterByRegistration: Map<string, string>;
  onChange: () => void;
}) {
  const linkedElsewhere = new Set(
    [...letterByRegistration.entries()].filter(([, l]) => l !== wine.letter).map(([id]) => id),
  );
  const hide = useMutation({
    mutationFn: () => tastingsApi.updateWine(eventId, wine.id, { revealed: false }),
    onSuccess: onChange,
  });

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Step n={1} title="Reveal the wine" done={wine.revealed}>
        {wine.revealed ? (
          <div className="space-y-2">
            <RevealedWine wine={wine} />
            <button
              type="button"
              className="btn-ghost px-3 py-1 text-xs"
              disabled={hide.isPending}
              onClick={() => hide.mutate()}
            >
              Hide wine
            </button>
          </div>
        ) : (
          <UnveilRegistration
            eventId={eventId}
            wine={wine}
            registrations={registrations}
            linkedElsewhere={linkedElsewhere}
            enabled={phase === 'revealing'}
            onChange={onChange}
          />
        )}
      </Step>
      <Step n={2} title="Who brought this?" done={wine.broughtByRevealed === true}>
        <BringerStep
          eventId={eventId}
          phase={phase}
          wine={wine}
          registrations={registrations}
          letterByRegistration={letterByRegistration}
          onChange={onChange}
        />
      </Step>
    </div>
  );
}

function Step({
  n,
  title,
  done,
  children,
}: {
  n: number;
  title: string;
  done: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`rounded-md border p-3 ${done ? 'border-gold-600/40 bg-gold-600/5' : 'border-ink-800'}`}
      aria-label={`Step ${n}: ${title}`}
    >
      <h3 className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-wide text-cream-300/70">
        <span
          className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${done ? 'bg-gold-600 text-on-gold' : 'border border-ink-800'}`}
          aria-hidden="true"
        >
          {done ? '✓' : n}
        </span>
        {title}
      </h3>
      {children}
    </section>
  );
}

function RevealedWine({ wine }: { wine: LiveWineView }) {
  // No "NV" for a missing vintage: a name typed into an older app may simply
  // not say which year it is.
  const line = [wine.vintage, wine.producer, wine.wineName].filter(Boolean).join(' ');
  return (
    <div className="flex items-start gap-3">
      {wine.labelImageUrl && (
        <img
          src={wine.labelImageUrl}
          alt={`Wine ${wine.letter} label`}
          className="h-16 w-12 shrink-0 rounded object-cover"
        />
      )}
      <div className="min-w-0 text-sm">
        <p className="text-cream-50">{line || `Wine ${wine.letter}`}</p>
        <p className="flex flex-wrap items-center gap-2 text-xs text-cream-300/60">
          {[wine.region, wine.varietal].filter(Boolean).join(' · ')}
          <WineColorChip color={wine.color} />
        </p>
      </div>
    </div>
  );
}

/** Option values: `reg:<registrationId>` names the bringer by registration, `person:<id>` by person. */
function bringerOptions(
  wine: LiveWineView,
  registrations: Registration[],
  letterByRegistration: Map<string, string>,
) {
  const options = registrations
    .filter((r) => r.status === 'registered')
    .map((r) => {
      const elsewhere = letterByRegistration.get(r.id);
      const onOtherLetter = elsewhere != null && elsewhere !== wine.letter;
      return {
        value: onOtherLetter ? `person:${r.personId}` : `reg:${r.id}`,
        personId: r.personId,
        label: `${r.firstName} ${r.lastName} — ${r.wineName || 'No wine entered'}${
          onOtherLetter ? ` (on wine ${elsewhere}; pick for a second bottle)` : ''
        }`,
      };
    });
  // Whoever is on the letter now, even if they aren't a registered attendee.
  if (wine.broughtByPersonId && !options.some((o) => o.personId === wine.broughtByPersonId)) {
    options.unshift({
      value: `person:${wine.broughtByPersonId}`,
      personId: wine.broughtByPersonId,
      label: wine.broughtByName ?? 'Current bringer',
    });
  }
  return options;
}

function currentBringer(wine: LiveWineView, options: { value: string; personId: string }[]) {
  if (wine.registrationId) return `reg:${wine.registrationId}`;
  if (wine.broughtByPersonId) {
    return options.find((o) => o.personId === wine.broughtByPersonId)?.value ?? '';
  }
  return '';
}

function BringerStep({
  eventId,
  phase,
  wine,
  registrations,
  letterByRegistration,
  onChange,
}: {
  eventId: string;
  phase: EventPhase;
  wine: LiveWineView;
  registrations: Registration[];
  letterByRegistration: Map<string, string>;
  onChange: () => void;
}) {
  const options = bringerOptions(wine, registrations, letterByRegistration);
  const assigned = currentBringer(wine, options);
  const [selected, setSelected] = useState(assigned);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched) setSelected(assigned);
  }, [assigned, touched]);

  const show = useMutation({
    mutationFn: () => {
      const [kind, id] = selected.split(':');
      return revealApi.revealBringer(
        eventId,
        wine.id,
        selected === assigned || !id
          ? {}
          : kind === 'reg'
            ? { registrationId: id }
            : { personId: id },
      );
    },
    onSuccess: () => {
      setTouched(false);
      onChange();
    },
  });
  const hide = useMutation({
    mutationFn: () => revealApi.hideBringer(eventId, wine.id),
    onSuccess: onChange,
  });

  if (wine.broughtByRevealed) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-cream-50">
          Brought by <span className="text-gold-200">{wine.broughtByName ?? 'someone'}</span>
        </p>
        <button
          type="button"
          className="btn-ghost px-3 py-1 text-xs"
          disabled={hide.isPending}
          onClick={() => hide.mutate()}
        >
          Hide name
        </button>
        {hide.error && (
          <p role="alert" className="text-xs text-danger">
            {hide.error.message}
          </p>
        )}
      </div>
    );
  }

  const ready = wine.revealed && (phase === 'revealing' || phase === 'final');
  return (
    <div className="space-y-2">
      <select
        className="field"
        aria-label={`Who brought wine ${wine.letter}`}
        value={selected}
        disabled={!wine.revealed}
        onChange={(e) => {
          setTouched(true);
          setSelected(e.target.value);
        }}
      >
        <option value="">Choose who brought it…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn bg-wine-700 px-3 py-1.5 text-xs text-on-wine hover:bg-wine-500"
        disabled={!ready || !selected || show.isPending}
        onClick={() => show.mutate()}
      >
        Show who brought it
      </button>
      {!wine.revealed && (
        <p className="text-xs text-cream-300/60">Reveal the wine first. The name stays hidden.</p>
      )}
      {show.error && (
        <p role="alert" className="text-xs text-danger">
          {show.error.message}
        </p>
      )}
    </div>
  );
}

/**
 * Bagging: which attendee's bottle is under a letter. Reveals nothing — the room
 * still sees only the letter until the host reveals it.
 */
export function SlotAssignSelect({
  eventId,
  wineId,
  letter,
  registrationId,
  registrations,
  letterByRegistration,
  disabled,
  onChange,
}: {
  eventId: string;
  wineId: string;
  letter: string;
  registrationId: string | null;
  registrations: Registration[];
  letterByRegistration: Map<string, string>;
  disabled?: boolean;
  onChange: () => void;
}) {
  const assign = useMutation({
    mutationFn: (id: string | null) => revealApi.assign(eventId, wineId, id),
    onSuccess: onChange,
  });
  const choices = registrations.filter((r) => {
    const on = letterByRegistration.get(r.id);
    return r.status === 'registered' && (on == null || on === letter);
  });
  return (
    <div>
      <select
        className="field"
        aria-label={`Whose wine is under ${letter}`}
        value={registrationId ?? ''}
        disabled={disabled || assign.isPending}
        onChange={(e) => assign.mutate(e.target.value || null)}
      >
        <option value="">Not bagged yet</option>
        {choices.map((r) => (
          <option key={r.id} value={r.id}>
            {r.firstName} {r.lastName} — {r.wineName || 'No wine entered'}
          </option>
        ))}
      </select>
      {assign.error && (
        <p role="alert" className="mt-1 text-xs text-danger">
          {assign.error.message}
        </p>
      )}
    </div>
  );
}
